import { useState, useEffect } from 'react';
import {
  TextField,
  Icon,
  Spinner,
  Snack,
  Snackbar,
  CheckboxGroup,
  Checkbox,
  RadioGroup,
  Radio,
  Label,
} from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useRevalidator } from 'react-router-dom';
import { without, uniq } from 'ramda';
import TokenizerExplainChart from '../../components/tokenizerExplainChart';
import { getTokenizerAnalysis } from '../../clientAPI';
import parseArrayFromUrlString from '../../utils/parseArrayFromUrlString';
import propagate403Error from '../../utils/propagate403Error';

const allDictionaryOptions = [
  'NIKE_TAXONOMY_DICT_CN',
  'NIKE_CUSTOM_DICT_CN',
  'NIKE_TYPOS_DICT_CN',
  'NIKE_REWRITE_DICT_CN',
  'NIKE_TAXONOMY_SYNONYMS_DICT_CN',
  'NIKE_NOGO_DICT_CN',
];
const allTokenizationOptions: string[] = [];
const defaultMode = 'INDEXING_ANALYSIS';

export default function Tokenizer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const [dictionaryOpts, setDictionaryOpts] = useState(
    (searchParams.get('dic') &&
      parseArrayFromUrlString(searchParams.get('dic'))) ||
      allDictionaryOptions,
  );
  const [tokenizationOpts, setTokenizationOpts] = useState(
    (searchParams.get('tokenization') &&
      parseArrayFromUrlString(searchParams.get('tokenization'))) ||
      allTokenizationOptions,
  );
  const [modeInput, setModeInput] = useState(
    searchParams.get('mode') || defaultMode,
  );
  const [terms, setTerms] = useState(searchParams.get('terms') || '');

  const [textinput, setTextInput] = useState(searchParams.get('terms') || '');
  const [showSnack, setShowSnack] = useState(false);
  const [errMessage, setErrMessage] = useState('');

  const { status, data, error } = useQuery({
    queryKey: [
      terms,
      modeInput,
      JSON.stringify(dictionaryOpts),
      JSON.stringify(tokenizationOpts),
    ],
    queryFn: async () => {
      return getTokenizerAnalysis(
        terms,
        modeInput,
        dictionaryOpts,
        tokenizationOpts,
      );
    },
    enabled: terms !== '' && dictionaryOpts.length > 0,
    onError: (err: keyable) => {
      setErrMessage(err.message || JSON.stringify(err));
      err.status === 401 && revalidator.revalidate();
    },
  });
  propagate403Error(error as keyable);
  const termsNum =
    status === 'success' && data.tokenizedResult?.tokens
      ? data.tokenizedResult.tokens.length
      : 0;
  const resultDivWidth = termsNum > 5 ? `${termsNum * 180}px` : '100%';
  useEffect(() => {
    if (status === 'error') {
      setShowSnack(true);
    } else {
      setShowSnack(false);
    }
  }, [status]);

  return (
    <>
      <section className='tw-text-center tw-py-8'>
        <form
          className='tw-w-1/3 tw-inline-flex tw-items-center tw-justify-center'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setTerms(textinput);
            searchParams.set('terms', textinput);
            setSearchParams(searchParams);
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <TextField
              className='tw-w-80 tw-rounded-3xl'
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setTextInput(val);
              }}
              id='input-text'
              errorMessage='This is an error message'
              hideLabel
              placeholder='Enter Text'
              label='tokenizer'
              defaultValue={textinput}
              beforeSlot={<Icon name='Search' />}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: '10px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Label font={'subtitle-1'} style={{ marginRight: '16px' }}>
                  Dictionary:
                </Label>
                <CheckboxGroup
                  id='dicts-checkbox'
                  label='dicts-checkbox'
                  hideLabel
                  style={{ display: 'flex', flexDirection: 'row' }}
                  onChange={(event) => {
                    const { target } = event;
                    let opts = [...dictionaryOpts];
                    if (target.checked) {
                      opts.push(target.value);
                      opts = uniq(opts);
                    } else {
                      opts = without([target.value], dictionaryOpts);
                    }
                    setDictionaryOpts(opts);
                    searchParams.set(
                      'dic',
                      encodeURIComponent(JSON.stringify(opts)),
                    );
                    setSearchParams(searchParams);
                  }}
                >
                  <Checkbox
                    label='TAXONOMY'
                    value='NIKE_TAXONOMY_DICT_CN'
                    id='taxonomy-checkbox'
                    checked={dictionaryOpts.includes('NIKE_TAXONOMY_DICT_CN')}
                  />
                  <Checkbox
                    label='TAXONOMY_SYNONYMS'
                    value='NIKE_TAXONOMY_SYNONYMS_DICT_CN'
                    id='taxonomy-synonyms-checkbox'
                    checked={dictionaryOpts.includes(
                      'NIKE_TAXONOMY_SYNONYMS_DICT_CN',
                    )}
                  />
                  <Checkbox
                    label='CUSTOM'
                    value='NIKE_CUSTOM_DICT_CN'
                    id='custom-checkbox'
                    checked={dictionaryOpts.includes('NIKE_CUSTOM_DICT_CN')}
                  />
                  <Checkbox
                    label='TYPOS'
                    value='NIKE_TYPOS_DICT_CN'
                    id='typo-checkbox'
                    checked={dictionaryOpts.includes('NIKE_TYPOS_DICT_CN')}
                  />
                  <Checkbox
                    label='REWRITE'
                    value='NIKE_REWRITE_DICT_CN'
                    id='rewrite-checkbox'
                    checked={dictionaryOpts.includes('NIKE_REWRITE_DICT_CN')}
                  />
                  <Checkbox
                    label='NOGO'
                    value='NIKE_NOGO_DICT_CN'
                    id='nogo-checkbox'
                    checked={dictionaryOpts.includes('NIKE_NOGO_DICT_CN')}
                  />
                  <Checkbox
                    label='ENGLISH'
                    value='NIKE_ENGLISH_DICT_CN'
                    id='english-checkbox'
                    checked={dictionaryOpts.includes('NIKE_ENGLISH_DICT_CN')}
                  />
                </CheckboxGroup>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginLeft: '50px',
                }}
              >
                <Label font={'subtitle-1'} style={{ marginRight: '16px' }}>
                  Mode:
                </Label>
                <RadioGroup
                  id='mode-radio'
                  label='mode-radio'
                  name='mode-radio'
                  hideLabel
                  style={{ display: 'flex', flexDirection: 'row' }}
                  valueSelected={modeInput}
                  onChange={(event) => {
                    setModeInput(event.target.value);
                    searchParams.set('mode', modeInput);
                    setSearchParams(searchParams);
                  }}
                >
                  <Radio label='BASE' value='BASE_ANALYSIS' id='base-radio' />
                  <Radio label='TO' value='TO_ANALYSIS' id='to-radio' />
                  <Radio
                    label='INDEXING'
                    value='INDEXING_ANALYSIS'
                    id='indexing-radio'
                  />
                  <Radio label='DIC' value='DIC_ANALYSIS' id='dic-radio' />
                </RadioGroup>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginLeft: '50px',
                }}
              >
                <Label font={'subtitle-1'} style={{ marginRight: '16px' }}>
                  Tokenization:
                </Label>
                <CheckboxGroup
                  id='dicts-checkbox'
                  label='dicts-checkbox'
                  hideLabel
                  style={{ display: 'flex', flexDirection: 'row' }}
                  onChange={(event) => {
                    const { target } = event;
                    let opts = [...tokenizationOpts];
                    let dicOpts = [...dictionaryOpts];
                    if (target.checked) {
                      opts.push(target.value);
                      opts = uniq(opts);

                      dicOpts.push('NIKE_ENGLISH_DICT_CN');
                      dicOpts = uniq(dicOpts);
                    } else {
                      opts = without([target.value], tokenizationOpts);
                      dicOpts = without(
                        ['NIKE_ENGLISH_DICT_CN'],
                        dictionaryOpts,
                      );
                    }

                    setTokenizationOpts(opts);
                    searchParams.set(
                      'tokenization',
                      encodeURIComponent(JSON.stringify(opts)),
                    );

                    setDictionaryOpts(dicOpts);
                    searchParams.set(
                      'dic',
                      encodeURIComponent(JSON.stringify(opts)),
                    );

                    setSearchParams(searchParams);
                  }}
                >
                  <Checkbox
                    label='ENGLISH'
                    value='ENGLISH'
                    id='english-checkbox'
                    checked={tokenizationOpts.includes('ENGLISH')}
                  />
                </CheckboxGroup>
              </div>
            </div>
          </div>
        </form>
        <Snackbar style={{ left: '90%', top: '90%' }}>
          {showSnack && (
            <Snack
              id='1'
              onDismiss={(id) => setShowSnack(false)}
              status='error'
            >
              {errMessage}
            </Snack>
          )}
        </Snackbar>
      </section>
      <section style={{ minWidth: resultDivWidth, overflow: 'scroll' }}>
        {status === 'success' && <TokenizerExplainChart data={data} />}
        {status === 'loading' && terms !== '' && dictionaryOpts.length > 0 && (
          <div className='tw-h-32 tw-w-full tw-flex tw-items-center tw-justify-center'>
            <Spinner size='large' />
          </div>
        )}
      </section>
    </>
  );
}
